import { Box, Center, Flex, Stack, Text } from '@mantine/core';
import CustomSpinner from '@/components/Spinner.tsx';
import React, { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import useAutomationId from '@/hooks/useAutomationId.ts';
import useRetrieveRunWithDatasets from '@/hooks/api/runs/useRetrieveRunWithDatasets.ts';
import { RunDatasetsSidebar } from '@/pages/Result/components/RunDatasetsSidebar.tsx';
import AutomationPageWrapper from '@/layouts/AutomationPageWrapper/AutomationPageWrapper.tsx';
import TabsLayout from './TabsLayout';
import { Actions, DockLocation, Model } from 'flexlayout-react';
import { initialJson, itemToTab } from '@/pages/Result/utils.ts';
import { useArrayQueryParam } from '@/hooks/useArrayQueryParam.ts';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlineDocumentText } from 'react-icons/hi2';
import { useAutomation } from '@/context/AutomationContext/useAutomationContext.ts';

const ResultPage = () => {
  const automation = useAutomation();

  const {
    data: runWithDatasets,
    isPending: loadingRun,
    error: runError,
  } = useRetrieveRunWithDatasets();
  const navigate = useNavigate();
  const automationId = useAutomationId();

  useEffect(() => {
    if (runError?.status === 404) {
      navigate(`/automations/${automationId}`);
      notifications.show({
        title: 'Run Not Found',
        message: 'The requested run does not exist.',
        color: 'yellow',
      });
    }
  }, [automationId, navigate, runError]);

  const { items } = useArrayQueryParam('datasetIds');

  const [model] = useState(() => Model.fromJson(initialJson(items) as any));

  const addTab = (item: string) => {
    const tab = itemToTab(item);
    if (model.getNodeById(tab.id)) {
      model.doAction(Actions.selectTab(tab.id));
    } else {
      const tabset = model.getActiveTabset();
      if (tabset === undefined) return;
      model.doAction(Actions.addNode(tab, tabset.getId(), DockLocation.CENTER, -1));
    }
  };

  if (loadingRun || !runWithDatasets) return <CustomSpinner />;

  return (
    <AutomationPageWrapper automation={automation}>
      <Flex w="100%" h="100%">
        <RunDatasetsSidebar runWithDatasets={runWithDatasets} addTab={addTab} />
        <Flex pos="relative" w="100%">
          <Box
            style={{ position: 'absolute', top: 0, left: 0, overflow: 'hidden' }}
            w="100%"
            h="100%"
          >
            <Center
              w="100%"
              h="100%"
              pos="absolute"
              bg={'white'}
              style={{
                zIndex: items.length === 0 ? 1 : -1,
                opacity: items.length === 0 ? 1 : 0,
                transition: 'opacity 0.2s',
              }}
            >
              <Stack w="250px" mb="100px">
                <AppIcon
                  Icon={HiOutlineDocumentText}
                  size={50}
                  ml="-6px"
                  iconProps={{ strokeWidth: 0.8 }}
                />
                <Text size="14px" fw={500}>
                  No documents have been selected
                </Text>
                <Text size="12px" c="#757575" lh="1.4">
                  To view a file or document, go to the documents sidebar
                </Text>
              </Stack>
            </Center>

            <TabsLayout model={model} />
          </Box>
        </Flex>
      </Flex>
    </AutomationPageWrapper>
  );
};

export default ResultPage;
